import React, { useState } from 'react';
import { CheckCircle, Loader, Calendar, User, Flag, FileText, Tag } from 'lucide-react';
import InlineEditField from '../shared/InlineEditField';
import StatusBadge, { PriorityBadge } from '../shared/StatusBadge';
import { updateTask, completeTask } from '../../services/perfex-api';

/**
 * Task Details Panel Component
 * Shows detailed task information with inline editing
 *
 * @param {Object} task - Selected task object
 * @param {function} onUpdate - Callback when task is updated (updatedTask) => void
 * @param {function} onComplete - Callback when task is marked complete
 */
const TaskDetailsPanel = ({ task, onUpdate, onComplete }) => {
    const [isCompleting, setIsCompleting] = useState(false);
    const [localTask, setLocalTask] = useState(task);

    if (!task) {
        return (
            <div className="flex items-center justify-center h-full bg-gray-50">
                <div className="text-center text-gray-500">
                    <FileText size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Select a task to view details</p>
                </div>
            </div>
        );
    }

    // Handle field update
    const handleFieldUpdate = async (fieldName, value) => {
        try {
            const updateData = { [fieldName]: value };
            const result = await updateTask(task.id, updateData);

            if (result.success) {
                const updatedTask = { ...localTask, [fieldName]: value };
                setLocalTask(updatedTask);
                onUpdate(updatedTask);
                return Promise.resolve();
            } else {
                throw new Error(result.data?.message || 'Failed to update task');
            }
        } catch (error) {
            console.error('Error updating task:', error);
            throw error;
        }
    };

    // Handle mark complete
    const handleMarkComplete = async () => {
        if (!confirm('Mark this task as complete?')) {
            return;
        }

        setIsCompleting(true);

        try {
            const result = await completeTask(task.id);

            if (result.success) {
                onComplete(task.id);
            } else {
                throw new Error('Failed to complete task');
            }
        } catch (error) {
            console.error('Error completing task:', error);
            alert('Failed to mark task as complete: ' + error.message);
        } finally {
            setIsCompleting(false);
        }
    };

    // Status options (Perfex default statuses)
    const statusOptions = [
        { value: '1', label: 'Not Started' },
        { value: '2', label: 'In Progress' },
        { value: '3', label: 'Testing' },
        { value: '4', label: 'Awaiting Feedback' },
        { value: '5', label: 'Complete' }
    ];

    // Priority options
    const priorityOptions = [
        { value: '1', label: 'Low' },
        { value: '2', label: 'Medium' },
        { value: '3', label: 'High' },
        { value: '4', label: 'Urgent' }
    ];

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {localTask.name || localTask.subject || 'Untitled Task'}
                        </h2>
                        <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge
                                status={localTask.status_name || statusOptions.find(s => s.value === String(localTask.status))?.label || 'Unknown'}
                            />
                            <PriorityBadge priority={localTask.priority || 2} />
                            <span className="text-sm text-gray-600">
                                Task #{localTask.id}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Task Name */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <FileText size={16} />
                        Task Name
                    </label>
                    <InlineEditField
                        value={localTask.name || localTask.subject || ''}
                        onSave={(value) => handleFieldUpdate('name', value)}
                        placeholder="Enter task name"
                        className="text-base"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <FileText size={16} />
                        Description
                    </label>
                    <InlineEditField
                        value={localTask.description?.replace(/<[^>]*>/g, '') || ''}
                        onSave={(value) => handleFieldUpdate('description', value)}
                        placeholder="Enter task description"
                        multiline={true}
                        className="text-sm"
                    />
                </div>

                {/* Status */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <CheckCircle size={16} />
                        Status
                    </label>
                    <InlineEditField
                        value={String(localTask.status || '1')}
                        onSave={(value) => handleFieldUpdate('status', value)}
                        type="select"
                        options={statusOptions}
                    />
                </div>

                {/* Priority */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Flag size={16} />
                        Priority
                    </label>
                    <InlineEditField
                        value={String(localTask.priority || '2')}
                        onSave={(value) => handleFieldUpdate('priority', value)}
                        type="select"
                        options={priorityOptions}
                    />
                </div>

                {/* Due Date */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Calendar size={16} />
                        Due Date
                    </label>
                    <InlineEditField
                        value={localTask.duedate || localTask.due_date || ''}
                        onSave={(value) => handleFieldUpdate('duedate', value)}
                        type="date"
                        placeholder="Set due date"
                    />
                </div>

                {/* Start Date */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Calendar size={16} />
                        Start Date
                    </label>
                    <InlineEditField
                        value={localTask.startdate || localTask.start_date || ''}
                        onSave={(value) => handleFieldUpdate('startdate', value)}
                        type="date"
                        placeholder="Set start date"
                    />
                </div>

                {/* Tags (if available) */}
                {localTask.tags && (
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                            <Tag size={16} />
                            Tags
                        </label>
                        <InlineEditField
                            value={localTask.tags || ''}
                            onSave={(value) => handleFieldUpdate('tags', value)}
                            placeholder="Enter tags (comma-separated)"
                        />
                    </div>
                )}

                {/* Additional Info */}
                <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                            <span>Created:</span>
                            <span className="font-medium">
                                {localTask.dateadded ? new Date(localTask.dateadded).toLocaleDateString() : 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>Last Updated:</span>
                            <span className="font-medium">
                                {localTask.dateupdated ? new Date(localTask.dateupdated).toLocaleDateString() : 'N/A'}
                            </span>
                        </div>
                        {localTask.assigned_name && (
                            <div className="flex justify-between">
                                <span>Assigned To:</span>
                                <span className="font-medium">{localTask.assigned_name}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer - Action Buttons */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                    onClick={handleMarkComplete}
                    disabled={isCompleting || localTask.status === 5 || localTask.status === '5'}
                    className="w-full py-4 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                    {isCompleting ? (
                        <>
                            <Loader className="animate-spin" size={24} />
                            Completing Task...
                        </>
                    ) : (
                        <>
                            <CheckCircle size={24} />
                            {localTask.status === 5 || localTask.status === '5' ? 'Task Completed' : 'Mark as Complete'}
                        </>
                    )}
                </button>

                {(localTask.status === 5 || localTask.status === '5') && (
                    <p className="text-center text-sm text-gray-600 mt-2">
                        This task has been marked as complete
                    </p>
                )}
            </div>
        </div>
    );
};

export default TaskDetailsPanel;
